import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';

import useLoanId from 'hooks/useLoanId';
import useUrlSearchParams from 'hooks/useUrlSearchParams';

import {
  selectApplicationStatusFromDeal,
  selectAusSummary,
  selectBorrowersCommonInfoGroupsByRefNumber,
  selectIsAusSubmitting,
  selectIsAusSummaryExist,
  selectIsLoanChangesLoaded,
  selectIsLoanChangesNotSaved,
  selectIsOBEngineType,
  selectIsSaveProgramPermissionsLack,
  selectLoanNumber,
  selectLoanPricingEngineType,
  selectNbesCommonInfoGroupsByRefNumber,
  selectPrimaryBorrowerIdCommonInfo,
} from 'store/loanApplications/selectors';

/**
 * Every loan-editor route and store read the loan-action run flow depends on. The loan header
 * uses it as-is; hosts outside the loan editor build the same shape (see useStandaloneRunnerContext).
 * Call it inside the loan-editor route — the params are empty in globally hosted dialogs.
 */
export const useLoanEditorRunnerContext = () => {
  const loanId = useLoanId();
  const { applicationId, subTab } = useParams();
  const { state: navigationState } = useLocation();
  const pathBeforeInitialRedirect = useRef(navigationState?.pathFrom);
  const searchParams = useUrlSearchParams();

  const borrowerGroups = useSelector(selectBorrowersCommonInfoGroupsByRefNumber);
  const nonBorrowerEntitiesGroups = useSelector(selectNbesCommonInfoGroupsByRefNumber);
  const primaryBorrowerId = useSelector(selectPrimaryBorrowerIdCommonInfo);
  const isSaveProgramPermissionsLack = useSelector(selectIsSaveProgramPermissionsLack);
  const isAusSubmitting = useSelector(selectIsAusSubmitting);
  const isAusSummaryExist = useSelector(selectIsAusSummaryExist);
  const ausSummary = useSelector(selectAusSummary);
  const isPendingChangesLoaded = useSelector(selectIsLoanChangesLoaded);
  const isPendingChanges = useSelector(selectIsLoanChangesNotSaved);
  const isOptimalBlueEngine = useSelector(selectIsOBEngineType);
  const applicationStatus = useSelector(selectApplicationStatusFromDeal);
  const loanNumber = useSelector(selectLoanNumber);
  const pricingEngineType = useSelector(selectLoanPricingEngineType);

  return useMemo(
    () => ({
      isLoanEditor: true,
      applicationId,
      loanId,
      subTab,
      pathBeforeInitialRedirect,
      searchParams,
      borrowerGroups,
      nonBorrowerEntitiesGroups,
      primaryBorrowerId,
      isSaveProgramPermissionsLack,
      isAusSubmitting,
      isAusSummaryExist,
      ausSummary,
      isPendingChangesLoaded,
      isPendingChanges,
      isOptimalBlueEngine,
      applicationStatus,
      loanNumber,
      pricingEngineType,
    }),
    [
      applicationId,
      loanId,
      subTab,
      searchParams,
      borrowerGroups,
      nonBorrowerEntitiesGroups,
      primaryBorrowerId,
      isSaveProgramPermissionsLack,
      isAusSubmitting,
      isAusSummaryExist,
      ausSummary,
      isPendingChangesLoaded,
      isPendingChanges,
      isOptimalBlueEngine,
      applicationStatus,
      loanNumber,
      pricingEngineType,
    ],
  );
};
